import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { useAppSelector, useAppDispatch } from "../../../../common/hooks/hooks";
import Breadcrumbs from "../../../../common/components/Breadcrumbs/Breadcrumbs";
import {
  selectCurrentStep,
  selectAppointmentSelection, // <-- 1. IMPORT NEW SELECTOR
} from "../checkoutSelectors";
import {
  setUseBillingForShipping,
  setCustomerNote,
  setCurrentStep,
  setAppointmentSelection,
} from "../checkoutSlice";
import { useSyncCheckoutStepToUrl } from "../hooks/useSyncCheckoutStepToUrl";
import { usePaymentError } from "../hooks/usePaymentError";
import { areAddressesEqual } from "../../../../common/helpers/addressHelpers";
import { useGetCheckoutDataQuery } from "../api/checkoutApi";
import { useGetCartQuery } from "../../cart/api/cartApi";

import CheckoutProgress from "./CheckoutProgress";
import CheckoutOrderSummary from "./CheckoutOrderSummary";

// --- Component Imports ---
import BillingStep from "./BillingStep/BillingStep";
import ShippingStep from "./ShippingStep/ShippingStep";
import AppointmentStep from "./AppointmentStep/AppointmentStep";
import PaymentStep from "./PaymentStep/PaymentStep";
import CheckoutApplyCouponForm from "./CheckoutApplyCouponForm";
import CheckoutPageSkeleton from "./CheckoutPageSkeleton";

const CheckoutPage: React.FC = () => {
  const { t, ready: areTranslationsReady } = useTranslation();
  const dispatch = useAppDispatch();
  const currentStep = useAppSelector(selectCurrentStep);

  // 2. GET OPTIMISTIC REDUX STATE
  const appointmentSelection = useAppSelector(selectAppointmentSelection);

  // --- 1. Detect and Handle Payment Errors from URL ---
  usePaymentError();

  // --- 2. Activate URL Synchronization ---
  useSyncCheckoutStepToUrl();

  // --- 3. UX: Scroll to top when step changes ---
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  // --- 4. Data Fetching ---
  const {
    data: checkoutData,
    isLoading: isCheckoutDataLoading,
    isError: isCheckoutDataError,
    refetch: refetchCheckout,
    isSuccess: isCheckoutSuccess,
  } = useGetCheckoutDataQuery();

  const {
    data: cart,
    isLoading: isCartLoading,
    isError: isCartError,
    isSuccess: isCartSuccess,
    refetch: refetchCart,
  } = useGetCartQuery();

  // --- 5. Step Guard: Skip features if not needed & Enforce requirements ---
  const requiresAppointment =
    checkoutData?.appointmentData?.requiresAppointment || false;

  // 3. FIX: Check BOTH the server data AND the optimistic Redux state.
  // This prevents the UI from bouncing the user while waiting for the server to echo the data back.
  const hasValidAppointment =
    !!checkoutData?.appointmentData?.date || !!appointmentSelection.date;

  useEffect(() => {
    // Wait for BOTH Cart AND Checkout data to be successfully loaded before guarding
    if (isCartSuccess && cart && isCheckoutSuccess && checkoutData) {
      // A. Guard against lingering on the shipping step if it's a digital-only cart
      if (!cart.needsShipping && currentStep === "shipping") {
        dispatch(
          setCurrentStep(requiresAppointment ? "appointment" : "payment"),
        );
      }
      // B. Guard against lingering on the appointment step if the cart has no digital items
      else if (!requiresAppointment && currentStep === "appointment") {
        dispatch(setCurrentStep("payment"));
      }
      // C. Guard against entering the payment step with missing/expired appointment data
      else if (
        requiresAppointment &&
        !hasValidAppointment &&
        currentStep === "payment"
      ) {
        dispatch(setCurrentStep("appointment"));
      }
    }
  }, [
    isCartSuccess,
    cart,
    isCheckoutSuccess,
    checkoutData,
    currentStep,
    requiresAppointment,
    hasValidAppointment,
    dispatch,
  ]);

  // --- 6. Initialize State from Server Data ---
  // We use a ref to ensure this only happens once when data first loads,
  // preventing user input from being overwritten by background refetches.
  const isStateInitialized = useRef(false);

  useEffect(() => {
    if (isCheckoutSuccess && checkoutData && !isStateInitialized.current) {
      // Initialize Address Toggle State
      const isSame = areAddressesEqual(
        checkoutData.billingAddress,
        checkoutData.shippingAddress,
      );
      dispatch(setUseBillingForShipping(isSame));

      // Initialize Customer Note State
      if (checkoutData.customerNote) {
        dispatch(setCustomerNote(checkoutData.customerNote));
      }

      // Initialize Appointment State from Server
      if (
        checkoutData.appointmentData?.date &&
        checkoutData.appointmentData?.time
      ) {
        dispatch(
          setAppointmentSelection({
            date: checkoutData.appointmentData.date,
            time: checkoutData.appointmentData.time,
          }),
        );
      }

      isStateInitialized.current = true;
    }
  }, [isCheckoutSuccess, checkoutData, dispatch]);

  const isComponentLoading =
    isCheckoutDataLoading || isCartLoading || !areTranslationsReady;
  const isError = isCheckoutDataError || isCartError;

  // Handler for Retry Button
  const handleRetry = () => {
    refetchCheckout();
    refetchCart();
  };

  if (isComponentLoading) {
    return <CheckoutPageSkeleton />;
  }

  // State: Empty Cart
  if (!cart || cart.itemsCount === 0) {
    return (
      <div className="checkout-page-container checkout-page--empty">
        <Breadcrumbs type="checkout" />
        <p className="checkout-page__empty-message">
          {t(
            "checkoutPage.emptyCart",
            "Your cart is empty. Please add items to proceed to checkout.",
          )}
        </p>
        <a
          href={window.commonVariables?.shopUrl || "/"}
          className="checkout-page__back-to-shop-btn"
        >
          {t("checkoutPage.backToShop", "Return to Shop")}
        </a>
      </div>
    );
  }

  // State: Error
  if (isError) {
    return (
      <div className="checkout-page-container">
        <Breadcrumbs type="checkout" />
        <div className="checkout-page__error-container">
          <p className="checkout-page__error-message">
            {t(
              "checkoutPage.errorLoading",
              "Error loading checkout data. Please try again.",
            )}
          </p>
          <button onClick={handleRetry} className="checkout-page__retry-btn">
            {t("checkoutPage.retry", "Retry")}
          </button>
        </div>
      </div>
    );
  }

  // Determine which step component to render dynamically
  let StepComponent: React.ComponentType | null = null;
  switch (currentStep) {
    case "billing":
      StepComponent = BillingStep;
      break;
    case "shipping":
      StepComponent = cart?.needsShipping
        ? ShippingStep
        : requiresAppointment
          ? AppointmentStep
          : PaymentStep;
      break;
    case "appointment":
      StepComponent = requiresAppointment ? AppointmentStep : PaymentStep;
      break;
    case "payment":
      StepComponent = PaymentStep;
      break;
    default:
      StepComponent = BillingStep;
  }

  const breadcrumbItems = [
    {
      label: t("breadcrumbs.shop", "Shop"),
      url: window.commonVariables?.shopUrl || "/",
    },
    {
      label: t("breadcrumbs.cart", "Cart"),
      url: window.commonVariables?.cartUrl || "/cart",
    },
    { label: t("breadcrumbs.checkout", "Checkout") },
  ];

  return (
    <div className="checkout-page-container">
      <Breadcrumbs items={breadcrumbItems} />

      <h1 className="checkout-page__title">
        {t("checkoutPage.title", "Checkout")}
      </h1>

      <CheckoutProgress currentStep={currentStep} />

      <div className="checkout-page__content">
        <main className="checkout-page__main">
          {StepComponent && <StepComponent />}
        </main>
        <aside className="checkout-page__sidebar">
          {cart && isCartSuccess ? (
            <>
              <CheckoutApplyCouponForm />
              <CheckoutOrderSummary cart={cart} />
              {/* Target for the Place Order button portal */}
              <div id="checkout-place-order-portal-root"></div>
            </>
          ) : (
            <Skeleton height={300} />
          )}
        </aside>
      </div>
    </div>
  );
};

export default CheckoutPage;
